// Business Status Badge Component
// Feature: 005-business-administration
// Updated: 007-cloudflare-migration - aligned with API status values

import { useTranslation } from 'react-i18next';
import type { BusinessStatus } from '@/types/businessAdmin';
import { CheckCircle, Clock, XCircle } from 'lucide-react';

interface BusinessStatusBadgeProps {
  status: BusinessStatus;
  className?: string;
}

export function BusinessStatusBadge({ status, className = '' }: BusinessStatusBadgeProps) {
  const { t } = useTranslation('businessAdmin');

  const statusConfig: Record<string, {
    label: string;
    icon: typeof CheckCircle;
    className: string;
  }> = {
    active: {
      label: t('status.active'),
      icon: CheckCircle,
      className: 'bg-green-100 text-green-800 border-green-200',
    },
    disabled: {
      label: t('status.disabled', 'Disabled'),
      icon: XCircle,
      className: 'bg-gray-100 text-gray-800 border-gray-200',
    },
    awaiting_moderation: {
      label: t('status.awaiting_moderation', 'Awaiting Moderation'),
      icon: Clock,
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    },
  };

  // Fallback for unknown/unexpected status values (defensive programming)
  const defaultConfig = {
    label: status || 'Unknown',
    icon: Clock,
    className: 'bg-gray-100 text-gray-500 border-gray-200',
  };

  const config = statusConfig[status] || defaultConfig;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${config.className} ${className}`}
    >
      <Icon size={14} />
      {config.label}
    </span>
  );
}
